if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.birdieSubscription = document.querySelector('.subscription-element');
        this.form = this.querySelector('form');
        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer') ||
          document.querySelector('cart-drawer-new');
        this.submitButton = this.querySelector('[type="submit"]');
        this.error = false;
        this.isSubmitting = false;
        this._originalLabelByBtn = new WeakMap();

        if (this.form) {
          this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        }

        if (this.submitButton) {
          if (this.cart) this.submitButton.setAttribute('aria-haspopup', 'dialog');
          // Capture clicks on the real submit AND Purchase Limit (.pol) clone.
          // Do NOT set disabled on click — that can cancel form submit.
          this.addEventListener(
            'click',
            (evt) => {
              const btn = evt.target.closest(
                'button.product-form__submit, [type="submit"][name="add"]'
              );
              if (!btn || (!this.contains(btn) && !this.getLoadingButtons().includes(btn))) {
                return;
              }
              if (this.isSubmitting) return;
              if (btn.disabled || this.submitButton.disabled) return;
              if (
                btn.getAttribute('aria-disabled') === 'true' ||
                this.submitButton.getAttribute('aria-disabled') === 'true'
              ) {
                return;
              }
              this.setLoading(true, { hardDisable: false });
            },
            true
          );
        }
      }

      uniqueID() {
        function chr4() {
          return (Date.now() * Math.random()).toString(16).slice(0, 4).toUpperCase();
        }
        return chr4() + '-' + chr4() + '-' + chr4();
      }

      /**
       * Purchase Limit (pol_zip) clones the ATC button as type="button".pol and
       * hides the real [type=submit]. Loading UI must target the visible clone.
       */
      getLoadingButtons() {
        const buttons = new Set();
        if (this.submitButton) buttons.add(this.submitButton);

        const scopes = [
          this,
          this.submitButton && this.submitButton.parentElement,
          this.querySelector('.product-form__buttons'),
        ].filter(Boolean);

        scopes.forEach((scope) => {
          scope.querySelectorAll('button.product-form__submit').forEach((btn) => {
            buttons.add(btn);
          });
        });

        return Array.from(buttons);
      }

      getSpinner(button = this.submitButton) {
        return button ? button.querySelector('.loading-overlay__spinner') : null;
      }

      getLabelSpan(button = this.submitButton) {
        if (!button) return null;
        return (
          button.querySelector(':scope > span') || button.querySelector('span')
        );
      }

      ensureLoadingStyles() {
        if (document.getElementById('atc-loading-style')) return;
        const style = document.createElement('style');
        style.id = 'atc-loading-style';
        style.textContent = `
          .product-form__submit.loading,
          .product-form__submit.pol.loading {
            position: relative !important;
            pointer-events: none !important;
            opacity: 0.85 !important;
            cursor: wait !important;
            color: transparent !important;
          }
          .product-form__submit.loading > span,
          .product-form__submit.pol.loading > span {
            visibility: hidden !important;
            opacity: 0 !important;
          }
          .product-form__submit.loading > .loading-overlay__spinner,
          .product-form__submit.pol.loading > .loading-overlay__spinner,
          .product-form__submit.loading > .loading-overlay__spinner:not(.hidden),
          .product-form__submit.pol.loading > .loading-overlay__spinner:not(.hidden) {
            display: flex !important;
            visibility: visible !important;
            opacity: 1 !important;
            position: absolute !important;
            inset: 0 !important;
            width: 100% !important;
            height: 100% !important;
            align-items: center !important;
            justify-content: center !important;
            z-index: 5 !important;
            pointer-events: none !important;
          }
          .product-form__submit.loading > .loading-overlay__spinner .spinner,
          .product-form__submit.pol.loading > .loading-overlay__spinner .spinner {
            width: 22px !important;
            height: 22px !important;
            display: block !important;
          }
          .product-form__submit.loading > .loading-overlay__spinner .path,
          .product-form__submit.pol.loading > .loading-overlay__spinner .path {
            stroke: #ffffff !important;
          }
        `;
        document.head.appendChild(style);
      }

      applyLoadingToButton(button, isLoading, hardDisable) {
        if (!button) return;
        const label = this.getLabelSpan(button);
        const spinner = this.getSpinner(button);
        const isHiddenOriginal =
          button === this.submitButton &&
          (button.style.display === 'none' ||
            window.getComputedStyle(button).display === 'none');

        if (isLoading) {
          button.setAttribute('aria-disabled', 'true');
          if (hardDisable) button.setAttribute('disabled', 'disabled');
          button.classList.add('loading');

          // Never fight POL's display:none on the original submit.
          if (!isHiddenOriginal) {
            button.style.setProperty('pointer-events', 'none', 'important');
            button.style.setProperty('opacity', '0.85', 'important');
            button.style.setProperty('cursor', 'wait', 'important');
            button.style.setProperty('position', 'relative', 'important');
            button.style.setProperty('color', 'transparent', 'important');
          }

          if (label) {
            label.classList.add('hidden');
            label.style.setProperty('visibility', 'hidden', 'important');
            label.style.setProperty('opacity', '0', 'important');
          }

          if (spinner) {
            spinner.classList.remove('hidden');
            spinner.style.setProperty('display', 'flex', 'important');
            spinner.style.setProperty('visibility', 'visible', 'important');
            spinner.style.setProperty('opacity', '1', 'important');
            spinner.style.setProperty('position', 'absolute', 'important');
            spinner.style.setProperty('inset', '0', 'important');
            spinner.style.setProperty('width', '100%', 'important');
            spinner.style.setProperty('height', '100%', 'important');
            spinner.style.setProperty('align-items', 'center', 'important');
            spinner.style.setProperty('justify-content', 'center', 'important');
            spinner.style.setProperty('z-index', '5', 'important');
            const svg = spinner.querySelector('svg, .spinner');
            if (svg) {
              svg.style.setProperty('width', '22px', 'important');
              svg.style.setProperty('height', '22px', 'important');
              svg.style.setProperty('display', 'block', 'important');
            }
            const path = spinner.querySelector('.path');
            if (path) path.style.setProperty('stroke', '#ffffff', 'important');
          } else if (label && !isHiddenOriginal) {
            if (!this._originalLabelByBtn.has(button)) {
              this._originalLabelByBtn.set(button, label.textContent);
            }
            label.classList.remove('hidden');
            label.textContent = 'Adding…';
            label.style.setProperty('visibility', 'visible', 'important');
            label.style.setProperty('opacity', '1', 'important');
            label.style.setProperty('color', '#ffffff', 'important');
            button.style.setProperty('color', '#ffffff', 'important');
          }
        } else {
          button.classList.remove('loading');
          ['pointer-events', 'opacity', 'cursor', 'position', 'color'].forEach((prop) => {
            button.style.removeProperty(prop);
          });
          if (!this.error) {
            button.removeAttribute('aria-disabled');
            button.removeAttribute('disabled');
          }
          if (spinner) {
            spinner.classList.add('hidden');
            spinner.removeAttribute('style');
            const svg = spinner.querySelector('svg, .spinner');
            if (svg) svg.removeAttribute('style');
            const path = spinner.querySelector('.path');
            if (path) path.removeAttribute('style');
          }
          if (label) {
            const original = this._originalLabelByBtn.get(button);
            if (original != null) {
              label.textContent = original;
              this._originalLabelByBtn.delete(button);
            }
            label.classList.remove('hidden');
            ['visibility', 'opacity', 'color'].forEach((prop) => {
              label.style.removeProperty(prop);
            });
          }
        }
      }

      setLoading(isLoading, options = {}) {
        if (!this.submitButton) return;
        this.ensureLoadingStyles();

        const hardDisable = options.hardDisable !== false;
        this.isSubmitting = !!isLoading;

        this.getLoadingButtons().forEach((button) => {
          this.applyLoadingToButton(button, isLoading, hardDisable);
        });
      }

      clearLoadingWhenReady() {
        const b2g1 = window.BuyXGetYFreeManager;

        const finish = () => {
          this.setLoading(false);
        };

        if (b2g1 && b2g1.isSyncing) {
          const started = Date.now();
          const poll = () => {
            if (!b2g1.isSyncing || Date.now() - started > 8000) {
              finish();
              return;
            }
            setTimeout(poll, 50);
          };
          setTimeout(poll, 50);
          return;
        }

        setTimeout(finish, 350);
      }

      getCartSections() {
        if (!this.cart || typeof this.cart.getSectionsToRender !== 'function') {
          return [];
        }
        try {
          return this.cart.getSectionsToRender().map((section) => section.id);
        } catch (e) {
          console.warn('[product-form] getSectionsToRender failed', e);
          return [];
        }
      }

      upsellProduct(formData) {
        fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (soldOutMessage) {
                this.submitButton.setAttribute('aria-disabled', 'true');
                this.submitButton.setAttribute('disabled', 'disabled');
                const label = this.getLabelSpan();
                if (label) label.classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
              }
              this.error = true;
              return;
            }

            if (!this.error) publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form' });
            this.error = false;
            if (this.cart && typeof this.cart.renderContents === 'function') {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
            this.error = true;
          })
          .finally(() => {
            if (this.cart && this.cart.classList.contains('is-empty')) {
              this.cart.classList.remove('is-empty');
            }
            this.clearLoadingWhenReady();
          });
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (!this.submitButton || !this.form) return;
        if (this.submitButton.getAttribute('data-product-form-locked') === 'true') return;

        this.handleErrorMessage();
        // Hard-disable only after submit has started so the click→submit chain works.
        this.setLoading(true, { hardDisable: true });
        this.submitButton.setAttribute('data-product-form-locked', 'true');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const sectionRender = this.getCartSections();

        if (this.cart && sectionRender.length) {
          formData.append('sections', sectionRender);
          formData.append('sections_url', window.location.pathname);
          if (typeof this.cart.setActiveElement === 'function') {
            this.cart.setActiveElement(document.activeElement);
          }
        }

        const UID = this.uniqueID();

        const unlock = () => {
          this.submitButton.removeAttribute('data-product-form-locked');
          if (this.cart && this.cart.classList.contains('is-empty')) {
            this.cart.classList.remove('is-empty');
          }
          this.clearLoadingWhenReady();
        };

        if (this.birdieSubscription) {
          const upsellFormData = {
            items: [
              {
                id: this.birdieSubscription.dataset.subscribe,
                selling_plan: this.birdieSubscription.dataset.selling,
                quantity: 1,
                properties: {
                  _UID: UID,
                  _theme: 'med',
                },
              },
            ],
            sections: sectionRender,
            sections_url: window.location.pathname,
          };

          this.upsellProduct(upsellFormData);
          this.submitButton.removeAttribute('data-product-form-locked');
          return;
        }

        config.body = formData;
        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (soldOutMessage) {
                this.submitButton.setAttribute('aria-disabled', 'true');
                this.submitButton.setAttribute('disabled', 'disabled');
                const label = this.getLabelSpan();
                if (label) label.classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
                this.error = true;
              } else {
                this.error = false;
              }
              return;
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error) publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form' });
            this.error = false;

            // Care Plan: if a warranty widget is active and has a mapped variant, add it
            this._addCarePlanIfActive(sectionRender);

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else if (typeof this.cart.renderContents === 'function') {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage('Could not add to cart. Please try again.');
            this.error = false;
          })
          .finally(() => {
            unlock();
          });
      }

      /**
       * Care Plan integration.
       * Business rules:
       * - Always add exactly qty 1 when Care Plan is toggled on
       * - At most one Care Plan per cart/order — skip if any Care Plan is already present
       */
      _addCarePlanIfActive(sectionRender) {
        if (!window.warrantyActive) return;

        const scope = this.closest('product-info') || document;
        const activeWidget = scope.querySelector(
          'warranty-widget.active[data-care-plan-variant-id]'
        ) || document.querySelector(
          'warranty-widget.active[data-care-plan-variant-id]'
        );

        if (!activeWidget) return;

        const variantId = parseInt(activeWidget.dataset.carePlanVariantId, 10);
        if (!variantId) return;

        const mainVariantId = parseInt(
          this.form.querySelector('[name="id"]')?.value || new FormData(this.form).get('id'),
          10
        );

        const cartRoot = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');

        const addCarePlan = () => {
          this.upsellProduct({
            items: [{
              id: variantId,
              quantity: 1,
              properties: {
                _care_plan: 'true',
                _exclude_from_promotions: 'true',
                _care_plan_for: String(mainVariantId || ''),
              },
            }],
            sections: sectionRender,
            sections_url: window.location.pathname,
          });
        };

        fetch(cartRoot + 'cart.js')
          .then((r) => r.json())
          .then((cart) => {
            const alreadyHasCarePlan = (cart.items || []).some(
              (item) => item.properties && String(item.properties._care_plan) === 'true'
            );
            if (alreadyHasCarePlan) return;
            addCarePlan();
          })
          .catch(() => {
            // If cart.js fails, fall back to DOM check then add
            if (document.querySelector('.care-plan-item')) return;
            addCarePlan();
          });
      }

      handleErrorMessage(errorMessage = false) {
        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}

// Keep at most one Care Plan in cart. Remove it only when no other products remain.
if (typeof window._carePlanCartSyncInit === 'undefined') {
  window._carePlanCartSyncInit = true;

  let _cpSyncing = false;

  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (event?.source === 'care-plan-sync' || _cpSyncing) return;

    const cpItems = document.querySelectorAll('.care-plan-item');
    if (!cpItems.length) return;

    const hasOtherProducts = !!document.querySelector(
      '.cart-item:not(.care-plan-item):not(.mystery-gift-item)'
    );

    const toUpdate = [];

    cpItems.forEach((el, index) => {
      const lineIndex = parseInt(el.id.replace('CartDrawer-Item-', ''), 10);
      const key = el.dataset.key;

      // One Care Plan per order: remove extras if somehow more than one exists
      if (index > 0) {
        toUpdate.push({ key, newQty: 0, lineIndex });
        return;
      }

      if (!hasOtherProducts) {
        // No remaining products — remove Care Plan
        toUpdate.push({ key, newQty: 0, lineIndex });
        return;
      }

      // Keep exactly qty 1
      const cpQty = parseInt(el.querySelector('.cart-drawer-new__qty-input')?.value, 10) || 0;
      if (cpQty !== 1) {
        toUpdate.push({ key, newQty: 1, lineIndex });
      }
    });

    if (!toUpdate.length) return;

    _cpSyncing = true;
    const cartEl = document.querySelector('cart-drawer-items') || document.querySelector('cart-items');

    Promise.all(
      toUpdate.map(({ key, newQty, lineIndex }) => {
        if (cartEl) {
          return cartEl.updateQuantity(lineIndex, newQty, '', key);
        }
        return fetch(window.Shopify.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: { [key]: newQty } }),
        });
      })
    )
      .catch(() => {})
      .finally(() => {
        if (!cartEl) publish(PUB_SUB_EVENTS.cartUpdate, { source: 'care-plan-sync' });
        _cpSyncing = false;
      });
  });
}
